// Ported from cartadigital-pos app/utils/order-formatting.ts.
// Adaptations for admin:
// - IOrderListItem lives in pos-types.ts
// - formatCartItemOptions takes the line's ISelectedOptionsMap (admin cart
//   lines don't carry the -pos flattened options[] array)
// - lucide icon names replaced with mdi names (rendered by PosUiIcon)
// - orderToListItem maps admin's IOrder onto the -pos list-item shape
import type { IMenuOption, IOrderListItem } from './pos-types';
import type { ISelectedOptionsMap } from './item-options-validation';
import { OrderPaymentStatus, OrderStatus, OrderType } from '@/enums/OrderEnums';
import { IOrder } from '@/interfaces/IOrder';
import { roundPrice } from '@/utils';

export interface IFormatDeliveryAddressLineInput {
  deliveryAddress: string | null;
  deliveryAddress2: string | null;
}

/**
 * Formats order items into a readable string preview
 * @param items Array of items with name and quantity
 * @param maxItems Maximum number of items to display before showing "+X more"
 * @returns Formatted string like "2x Pizza, 1x Coca Cola" or "2x Pizza, +3 more"
 */
export function formatOrderItemsPreview(
  items: Array<{ name: string; quantity: number }>,
  maxItems: number = 3
): string {
  if (!items || items.length === 0) {
    return '';
  }

  const displayItems = items.slice(0, maxItems);
  const remainingCount = items.length - maxItems;

  const itemsText = displayItems
    .map((item) => `${item.quantity}x ${item.name}`)
    .join(', ');

  if (remainingCount > 0) {
    return `${itemsText}, +${remainingCount} más`;
  }

  return itemsText;
}

/**
 * Bare table names (e.g. "1") are confusing in titles — prepend the
 * localized "table" word (posV2.tableWord) unless the name already
 * contains it.
 */
export function formatTableDisplayName(
  tableName: string,
  tableWord: string
): string {
  return tableName.toLowerCase().includes(tableWord.toLowerCase())
    ? tableName
    : `${tableWord} ${tableName}`;
}

export interface IPaymentStatusInfo {
  label: string;
  /** Admin extra: posV2.* key so components can render translated copy. */
  i18nKey: string;
  color: 'primary' | 'success' | 'warning' | 'error' | 'neutral';
}

/**
 * Gets the payment status display information
 */
export function getPaymentStatusInfo(
  status: string | null | undefined
): IPaymentStatusInfo {
  switch (status) {
    case OrderPaymentStatus.Paid:
      return { label: 'Pagado', i18nKey: 'posV2.payPaid', color: 'success' };
    case OrderPaymentStatus.Partial:
      return {
        label: 'Parcial',
        i18nKey: 'posV2.payPartial',
        color: 'warning',
      };
    case OrderPaymentStatus.Awaiting:
      return {
        label: 'Pendiente',
        i18nKey: 'posV2.payAwaiting',
        color: 'neutral',
      };
    case OrderPaymentStatus.Declined:
      return {
        label: 'Rechazado',
        i18nKey: 'posV2.payDeclined',
        color: 'error',
      };
    case OrderPaymentStatus.Reimbursed:
      return {
        label: 'Reembolsado',
        i18nKey: 'posV2.payReimbursed',
        color: 'neutral',
      };
    default:
      return {
        label: 'Pendiente',
        i18nKey: 'posV2.payAwaiting',
        color: 'neutral',
      };
  }
}

/**
 * Payment status info with client-derived "Parcial": the server never writes
 * `OrderPaymentStatus.Partial` (legacy-unused enum value) — a partially paid
 * order (e.g. checkout club-balance debit + in-person remainder) is
 * `totalPaid > 0` while still `Awaiting`.
 */
export function getEffectivePaymentStatusInfo(
  order: Pick<IOrder, 'paymentStatus' | 'totalPaid'>
): IPaymentStatusInfo {
  const totalPaid = Number(order.totalPaid ?? 0);
  const isPartial =
    totalPaid > 0 && order.paymentStatus !== OrderPaymentStatus.Paid;

  return getPaymentStatusInfo(
    isPartial ? OrderPaymentStatus.Partial : order.paymentStatus
  );
}

/**
 * Formats a cart line's selected options using full menu options metadata.
 * Example output: "Queso extra, Sin cebolla, 2x Salsa picante"
 */
export function formatCartItemOptions(
  optionValues: ISelectedOptionsMap,
  options: IMenuOption[]
): string {
  if (!optionValues || Object.keys(optionValues).length === 0) return '';

  const parts: string[] = [];

  for (const group of options) {
    const selectedItems = optionValues[String(group.id)];
    if (!selectedItems) continue;

    const type = String(group.type ?? '')
      .trim()
      .toLowerCase();

    for (const opt of group.items) {
      const rawVal = selectedItems[String(opt.id)];
      const val =
        typeof rawVal === 'number' && Number.isFinite(rawVal)
          ? Math.max(0, Math.trunc(rawVal))
          : 0;
      if (val <= 0) continue;

      const title = String(opt.title ?? '').trim();
      if (!title) continue;

      // Match legacy semantics: show "1x" for Adicionales (type="add").
      // For other option types, show quantity only when val > 1.
      const isAdd = type === 'add';
      const qtyPrefix = isAdd || val > 1 ? `${val}x ` : '';
      parts.push(`${qtyPrefix}${title}`);
    }
  }

  return parts.join(', ');
}

/**
 * Fallback formatter when we don't have options metadata yet.
 * Shows a compact count like: "3 opciones".
 */
export function formatCartItemOptionsSimple(
  optionValues: ISelectedOptionsMap
): string {
  if (!optionValues) return '';

  let total = 0;
  let keys = 0;
  for (const inner of Object.values(optionValues)) {
    for (const val of Object.values(inner)) {
      keys += 1;
      total += Math.max(0, val);
    }
  }
  if (keys === 0) return '';

  const count = total > 0 ? total : keys;
  return count === 1 ? '1 opción' : `${count} opciones`;
}

export function formatDeliveryAddressLine(
  input: IFormatDeliveryAddressLineInput
): string {
  const a1 = String(input.deliveryAddress ?? '').trim();
  const a2 = String(input.deliveryAddress2 ?? '').trim();

  if (!a1 && !a2) return '';
  if (!a1) return a2;
  if (!a2) return a1;
  return `${a1} - ${a2}`;
}

const normalizeComparable = (s: string): string => {
  return s.trim().toLowerCase();
};

const formatLocalIdShort = (localId: string): string => {
  const s = localId.trim();
  if (!s) return '#---';
  return `#${s.slice(-6).toUpperCase()}`;
};

const normalizeOrderType = (t: unknown): OrderType => {
  const s = String(t ?? '')
    .trim()
    .toLowerCase();
  if (s === OrderType.Away) return OrderType.Away;
  if (s === OrderType.Delivery) return OrderType.Delivery;
  if (s === OrderType.Seat) return OrderType.Seat;
  return OrderType.Seat;
};

const pickCustomerLabel = (
  customerName: string,
  customerPhone: string
): string | null => {
  if (customerName.trim()) return customerName.trim();
  if (customerPhone.trim()) return customerPhone.trim();
  return null;
};

export interface IOrderHumanTitleInput {
  type: OrderType | string | null | undefined;
  humanId?: string | null;
  tableName?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  deliveryAddress?: string | null;
  deliveryAddress2?: string | null;
}

export function getOrderHumanTitle(input: IOrderHumanTitleInput): string {
  const type = normalizeOrderType(input.type);

  const humanId = String(input.humanId ?? '').trim();
  const tableName = String(input.tableName ?? '').trim();
  const customerName = String(input.customerName ?? '').trim();
  const customerPhone = String(input.customerPhone ?? '').trim();

  const addressLine = formatDeliveryAddressLine({
    deliveryAddress: input.deliveryAddress ?? null,
    deliveryAddress2: input.deliveryAddress2 ?? null,
  });

  if (type === OrderType.Away) {
    const suffix = pickCustomerLabel(customerName, customerPhone);

    if (humanId) {
      const base = `Llevar #${humanId}`;
      return suffix ? `${base} · ${suffix}` : base;
    }

    if (suffix) return `Llevar · ${suffix}`;
    return 'Llevar';
  }

  if (type === OrderType.Delivery) {
    if (addressLine) return `Delivery · ${addressLine}`;
    const suffix = pickCustomerLabel(customerName, customerPhone);
    return suffix ? `Delivery · ${suffix}` : 'Delivery';
  }

  // Seat
  const base = tableName ? `Mesa ${tableName}` : 'Mesa';
  return customerName ? `${base} · ${customerName}` : base;
}

/**
 * Human title of a persisted order. `tableName` is resolved by the caller
 * (`order.table` is joined by the API, the restaurant's tables are the fallback).
 */
export function getOrderTitleForOrder(
  order: Pick<
    IOrder,
    | 'type'
    | 'humanId'
    | 'userName'
    | 'userTel'
    | 'userWhatsapp'
    | 'userAddress'
    | 'userAddress2'
  >,
  tableName: string | null
): string {
  return getOrderHumanTitle({
    type: order.type,
    humanId: order.humanId ?? null,
    tableName,
    customerName: order.userName ?? null,
    customerPhone: order.userTel || order.userWhatsapp || null,
    deliveryAddress: order.userAddress ?? null,
    deliveryAddress2: order.userAddress2 ?? null,
  });
}

export function getOrderCardHeaderText(
  order: Pick<
    IOrderListItem,
    | 'type'
    | 'humanId'
    | 'serverId'
    | 'localId'
    | 'customerName'
    | 'tableName'
    | 'deliveryAddress'
    | 'deliveryAddress2'
  >
): string {
  const type = order.type as OrderType;

  if (type === OrderType.Away) {
    const humanId = String(order.humanId ?? '').trim();
    if (humanId) return `#${humanId}`;

    const serverId = Number(order.serverId ?? 0);
    if (Number.isFinite(serverId) && serverId > 0) return `#${serverId}`;

    const localId = String(order.localId ?? '').trim();
    return localId ? formatLocalIdShort(localId) : '#---';
  }

  if (type === OrderType.Delivery) {
    const addr = formatDeliveryAddressLine({
      deliveryAddress: order.deliveryAddress ?? null,
      deliveryAddress2: order.deliveryAddress2 ?? null,
    });
    if (addr) return addr;

    const customerName = String(order.customerName ?? '').trim();
    if (customerName) return customerName;

    return 'Delivery';
  }

  // Seat (table)
  const customerName = String(order.customerName ?? '').trim();
  if (customerName) return customerName;

  const tableName = String(order.tableName ?? '').trim();
  if (tableName) return tableName;

  return 'Mesa';
}

export interface IOrderCardBodyLine {
  icon: string;
  text: string;
}

export function getOrderCardBodyCustomerLine(
  order: Pick<
    IOrderListItem,
    'type' | 'customerName' | 'customerPhone' | 'tableName'
  >,
  headerText: string
): IOrderCardBodyLine | null {
  const type = order.type as OrderType;

  const headerNorm = normalizeComparable(headerText);
  const customerName = String(order.customerName ?? '').trim();
  const customerPhone = String(order.customerPhone ?? '').trim();
  const tableName = String(order.tableName ?? '').trim();

  const customerNameNorm = normalizeComparable(customerName);
  const tableNameNorm = normalizeComparable(tableName);

  // Seat: header is either customerName or tableName. Never repeat that same field in body.
  if (type === OrderType.Seat) {
    if (customerName && headerNorm === customerNameNorm) {
      if (customerPhone) return { icon: 'phone', text: customerPhone };
      return null;
    }

    if (tableName && headerNorm === tableNameNorm) {
      if (customerName) return { icon: 'account', text: customerName };
      if (customerPhone) return { icon: 'phone', text: customerPhone };
      return null;
    }

    // Fallback: if header is neither, show best available customer info without duplicating header.
    if (customerName && headerNorm !== customerNameNorm)
      return { icon: 'account', text: customerName };
    if (customerPhone && headerNorm !== normalizeComparable(customerPhone))
      return { icon: 'phone', text: customerPhone };
    return null;
  }

  // Away/Delivery: show customer name, else phone; avoid duplicating header if it happened to match.
  if (customerName && headerNorm !== customerNameNorm)
    return { icon: 'account', text: customerName };
  if (customerPhone && headerNorm !== normalizeComparable(customerPhone))
    return { icon: 'phone', text: customerPhone };

  return null;
}

export interface IOrderTabDisplayModelInput {
  type: OrderType | string | null | undefined;

  humanId?: string | null;
  serverId?: number | null;
  localId?: string | null;

  customerName?: string | null;
  customerPhone?: string | null;

  tableName?: string | null;

  deliveryAddress?: string | null;
  deliveryAddress2?: string | null;

  /**
   * When true and type=away, prefer human-friendly draft fallback (name/phone/Llevar)
   * instead of falling back to #serverId/#localId.
   */
  preferAwayDraftFallback?: boolean;
}

export interface IOrderTabDisplayModel {
  kindLabel: string; // Mesa | Llevar | Delivery
  titleText: string; // main title (bottom line)
  titleIcon: string; // icon for bottom line (mdi name)
}

export function getOrderTabDisplayModel(
  input: IOrderTabDisplayModelInput
): IOrderTabDisplayModel {
  const type = normalizeOrderType(input.type);

  const kindLabel =
    type === OrderType.Away
      ? 'Llevar'
      : type === OrderType.Delivery
      ? 'Delivery'
      : 'Mesa';

  const customerName = String(input.customerName ?? '').trim();
  const customerPhone = String(input.customerPhone ?? '').trim();
  const tableName = String(input.tableName ?? '').trim();

  const titleBase: Pick<
    IOrderListItem,
    | 'type'
    | 'humanId'
    | 'serverId'
    | 'localId'
    | 'customerName'
    | 'tableName'
    | 'deliveryAddress'
    | 'deliveryAddress2'
  > = {
    type,
    humanId: input.humanId ?? null,
    serverId: input.serverId ?? null,
    localId: input.localId ?? null,
    customerName: input.customerName ?? null,
    tableName: input.tableName ?? null,
    deliveryAddress: input.deliveryAddress ?? null,
    deliveryAddress2: input.deliveryAddress2 ?? null,
  };

  let titleText = '';

  if (type === OrderType.Away) {
    const humanId = String(input.humanId ?? '').trim();
    if (humanId) {
      const suffix = pickCustomerLabel(customerName, customerPhone);
      titleText = suffix ? `#${humanId} · ${suffix}` : `#${humanId}`;
    } else if (input.preferAwayDraftFallback) {
      if (customerName) titleText = customerName;
      else if (customerPhone) titleText = customerPhone;
      else titleText = 'Llevar';
    } else {
      titleText = getOrderCardHeaderText(titleBase);
    }
  } else if (type === OrderType.Seat) {
    // Tabs: always include table name when available.
    if (tableName && customerName) titleText = `${tableName} - ${customerName}`;
    else if (tableName) titleText = tableName;
    else if (customerName) titleText = customerName;
    else titleText = 'Mesa';
  } else {
    titleText = getOrderCardHeaderText(titleBase);
  }

  const titleIcon =
    type === OrderType.Away
      ? 'shopping'
      : type === OrderType.Delivery
      ? 'map-marker'
      : tableName
      ? 'silverware-fork-knife'
      : customerName &&
        normalizeComparable(customerName) === normalizeComparable(titleText)
      ? 'account'
      : 'silverware-fork-knife';

  return { kindLabel, titleText, titleIcon };
}

export interface IOrderTabDisambiguationModel extends IOrderTabDisplayModel {
  key: string;
  isPersisted: boolean;
  timeMs: number | null; // used for persisted duplicates
}

export interface IOrderTabDisambiguatedModel
  extends IOrderTabDisambiguationModel {
  titleSuffix?: string; // e.g. "· 18:12" or "· 2"
}

const formatTimeHHmm = (ms: number): string => {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

export function applyTabDisambiguation(
  models: IOrderTabDisambiguationModel[]
): IOrderTabDisambiguatedModel[] {
  const out: IOrderTabDisambiguatedModel[] = models.map((m) => ({ ...m }));

  const originalIndex = new Map<string, number>();
  out.forEach((m, i) => originalIndex.set(m.key, i));

  const groups = new Map<string, IOrderTabDisambiguatedModel[]>();
  for (const m of out) {
    const gk = `${normalizeComparable(m.kindLabel)}|${normalizeComparable(
      m.titleText
    )}`;
    const arr = groups.get(gk);
    if (arr) arr.push(m);
    else groups.set(gk, [m]);
  }

  for (const arr of groups.values()) {
    if (arr.length <= 1) continue;

    // Stable within session: keep original render order.
    arr.sort(
      (a, b) =>
        Number(originalIndex.get(a.key) ?? 0) -
        Number(originalIndex.get(b.key) ?? 0)
    );

    // Persisted duplicates: suffix by time (helps differentiate without ids).
    for (const m of arr) {
      if (!m.isPersisted) continue;
      if (typeof m.timeMs === 'number' && m.timeMs > 0) {
        m.titleSuffix = `· ${formatTimeHHmm(m.timeMs)}`;
      } else {
        m.titleSuffix = '·';
      }
    }

    // Draft duplicates: suffix from the 2nd onward.
    let draftN = 0;
    for (const m of arr) {
      if (m.isPersisted) continue;
      draftN += 1;
      if (draftN <= 1) continue;
      m.titleSuffix = `· ${draftN}`;
    }
  }

  return out;
}

/**
 * Derives the discount badge shown on order cards. A single uncapped
 * percentage row keeps its "%" reading; everything else (fixed, multiple
 * rows, capped coupons, legacy row-less orders) shows the server-computed
 * effective amount — that amount is the truth.
 */
export function deriveOrderDiscountBadge(
  order: Pick<
    IOrder,
    'discounts' | 'discountedAmount' | 'discountAmount' | 'discount'
  >
): IOrderListItem['discountBadge'] {
  const rows = order.discounts ?? [];
  const effective = roundPrice(
    order.discountedAmount ?? order.discountAmount ?? 0
  );

  if (
    rows.length === 1 &&
    rows[0].isPercentage &&
    typeof rows[0].metadata?.maxDiscountAmount !== 'number' &&
    effective > 0
  ) {
    return { kind: 'percent', value: rows[0].value };
  }

  if (effective > 0) {
    return { kind: 'amount', value: effective };
  }

  // Legacy orders predating discount rows carry only the percentage field.
  if (rows.length === 0 && effective === 0 && order.discount > 0) {
    return { kind: 'percent', value: order.discount };
  }

  return null;
}

/**
 * Maps an admin IOrder onto the -pos IOrderListItem shape consumed by the
 * ported grouping/formatting helpers.
 */
export function orderToListItem(order: IOrder): IOrderListItem {
  const createdAtMs = new Date(order.createdAt).getTime();
  const updatedAtMs = order.updatedAt
    ? new Date(order.updatedAt).getTime()
    : createdAtMs;

  return {
    id: order.id,
    humanId: order.humanId ?? null,
    localId: order.localId ?? null,
    serverId: order.id,
    type: order.type,
    status: order.status,
    total: order.total,
    totalPaid: order.totalPaid,
    createdAt: Number.isFinite(createdAtMs) ? createdAtMs : 0,
    updatedAt: Number.isFinite(updatedAtMs) ? updatedAtMs : 0,
    needsAttention: order.status === OrderStatus.Received,
    tableId: order.tableId ?? null,
    tableName: order.table?.name ?? null,
    customerName: order.userName ?? null,
    customerPhone: order.userTel ?? null,
    deliveryAddress: order.userAddress ?? null,
    deliveryAddress2: order.userAddress2 ?? null,
    comment: order.comment ?? null,
    paymentStatus: order.paymentStatus ?? null,
    discountBadge: deriveOrderDiscountBadge(order),
    items: (order.orderItems ?? [])
      .filter((oi) => !oi.canceledAt)
      .map((oi) => ({
        name: oi.item?.name ?? '',
        quantity: oi.amount,
      })),
  };
}
